/**
 * QuestionnaireAlertsInfoModal
 *
 * Information modal that explains how questionnaire alerts work in the Daily Brief.
 * Provides an example scenario and links to support documentation for more details.
 *
 * @component
 * @param {boolean} isOpen - Whether the modal is currently visible
 * @param {Function} onClose - Callback function when modal is closed
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Modal from '../../../shared/_TBComponents/Modal';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const ModalContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  gap: 48px;
  width: 100%;
`;

const ContentSection = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  align-self: stretch;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const TitleRow = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 32px;
  align-self: stretch;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const Title = styled('h2')`
  margin: 0;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 33px;
  letter-spacing: -0.01em;
  color: #444444;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const CloseIcon = styled('button')`
  width: 16px;
  height: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  flex: none;
  order: 1;
  flex-grow: 0;

  svg {
    display: block;
    width: 100%;
    height: 100%;
    color: #9e9e9e;
    fill: #9e9e9e;
  }

  &:hover svg {
    color: #444444;
    fill: #444444;
  }
`;

const Description = styled('p')`
  margin: 0 0 16px 0;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #444444;
  flex: none;
  flex-grow: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  align-self: stretch;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const PrimaryButton = styled('button')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 16px;
  gap: 10px;
  height: 36px;
  background: #444444;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  align-self: stretch;
  transition: background 0.2s ease;

  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0.03em;
  color: #ffffff;
  flex: none;
  order: 0;
  flex-grow: 0;

  &:hover {
    background: #333333;
  }
`;

const SecondaryButton = styled('button')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 16px;
  gap: 10px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  align-self: stretch;
  transition: color 0.2s ease;

  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 150%;
  letter-spacing: 0.03em;
  color: #9e9e9e;
  flex: none;
  order: 1;
  flex-grow: 0;

  &:hover {
    color: #444444;
  }
`;

const QuestionnaireAlertsInfoModal = ({ isOpen, onClose }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    hideButton
    hideTitle
    customMinHeight='auto'
    customMaxWidth='600px'
    customPadding='0px'
    customBorderRadius='8px'
    flexGrow={false}
  >
    <ModalContent>
      <ContentSection>
        <TitleRow>
          <Title>What Are Questionnaire Alerts?</Title>
          <CloseIcon type='button' aria-label='Close modal' onClick={onClose}>
            <IcomoonReact iconSet={iconSet} size={16} icon='remove' />
          </CloseIcon>
        </TitleRow>
        <Description>
          If you assign questions to be answered by athletes, you&apos;ll see any answers
          from the last two days that were over the defined threshold here.
        </Description>
        <Description>
          For example, if you assigned the question &quot;How many hours of sleep did you
          get?&quot;, and the threshold was &quot;less than 5 hours&quot;, and the code for
          the question is SLEEP, then you would see &quot;SLEEP&quot; here for anyone who
          reported sleeping less than 5 hours yesterday or today.
        </Description>
      </ContentSection>

      <ButtonContainer>
        <PrimaryButton type='button' onClick={onClose}>
          CLOSE
        </PrimaryButton>
        <SecondaryButton
          type='button'
          as='a'
          href='https://support.teambuildr.com/article/qmn1676hap-how-to-add-questions-and-questionnaires-in-the-exercise-database'
          target='_blank'
          rel='noopener noreferrer'
        >
          LEARN MORE
        </SecondaryButton>
      </ButtonContainer>
    </ModalContent>
  </Modal>
);

QuestionnaireAlertsInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QuestionnaireAlertsInfoModal;
